/**
 * anomalyUtils.js — compares what people actually clocked (`shifts`) with what they were
 * assigned in the planner (`scheduledShifts`) and flags anything worth a manager's look.
 * Pure (no Firebase); unit-tested in anomalyUtils.test.js.
 *
 * Within ±1 hour of the assigned times is fine; beyond that it's an anomaly. Also flagged:
 * no assignment at all, no-shows, overlapping / 0-minute records, a clock-in that runs
 * through a split-shift break, forgotten clock-outs, very long shifts or breaks, and
 * weekly overtime on actual hours.
 *
 * How records meet the schedule: per person per day, assigned shifts and clock records
 * that overlap in time form a group. A group is compared as a whole — first clock-in vs
 * first assigned start, last clock-out vs last assigned end, worked vs assigned minutes —
 * so split shifts, mid-shift re-punches and duplicates are judged sensibly.
 */
import { toDate, workedMinutes, breakMinutesWithin } from './timesheetUtils';

export const ANOMALY_TOLERANCE_MIN = 60;
const LONG_SHIFT_MIN = 12 * 60;
const OPEN_TOO_LONG_MIN = 12 * 60;
const LONG_BREAK_MIN = 60;
const WEEKLY_OVERTIME_MIN = 40 * 60;
const MISTAP_MIN = 2;

const pad = n => String(n).padStart(2, '0');
export const isoDay = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const minutes = ms => Math.round(ms / 60000);
const isOpen = s => s.status === 'active' || s.status === 'on-break';

export function fmtDur(m) {
  const a = Math.abs(Math.round(m));
  const h = Math.floor(a / 60), r = a % 60;
  if (!h) return `${r}m`;
  return r ? `${h}h ${r}m` : `${h}h`;
}
const fmtClock = d => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
const fmtRange = (a, b) => `${fmtClock(a)} – ${b ? fmtClock(b) : 'now'}`;
const fmtDayLabel = iso => new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

function mondayISO(d) {
  const m = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  m.setDate(m.getDate() - (m.getDay() === 0 ? 6 : m.getDay() - 1));
  return isoDay(m);
}

/** An assigned shift as a time window (an end at/before the start means it ends after midnight). */
function schedWindow(p) {
  const start = new Date(`${p.date}T${p.startTime}:00`);
  const end = new Date(`${p.date}T${p.endTime}:00`);
  if (end <= start) end.setDate(end.getDate() + 1);
  return { start, end };
}

function recordWorked(r, start, end) {
  if (!isOpen(r) && typeof r.totalMinutes === 'number') return r.totalMinutes;
  return workedMinutes(start, end, r.breaks || []);
}

// Severity drives colour and sort: high first
const SEVERITY = {
  'no-show': 'high', unscheduled: 'high', 'open-long': 'high', overlap: 'high', 'covers-break': 'high',
  missing: 'medium', 'late-in': 'medium', 'early-in': 'medium', 'early-out': 'medium', 'late-out': 'medium',
  'over-hours': 'medium', 'under-hours': 'medium', 'long-shift': 'medium', 'long-break': 'medium',
  'mis-tap': 'medium', overtime: 'medium',
};
const issue = (kind, label, detail) => ({ kind, label, detail, severity: SEVERITY[kind] || 'medium' });

/**
 * @param records      live clock records (deleted ones already excluded)
 * @param scheduled    assigned shifts in the period ({ id, userId, userName, date, startTime, endTime, status })
 * @param scheduledDays every date in the period that has ANY assigned shift (for anyone) — days with
 *                     none mean the planner wasn't used, so they're not compared (else every punch = "not scheduled")
 * @returns { entries, byRecord, unplannedDays }
 *   entries   — one per group / no-show / person-week: { key, userId, userName, day, start, issues[], recordIds[],
 *               scheduledIds[], schedText, actualText, reviewed }
 *   byRecord  — Map(recordId → issues[]) for badges on the Shift Log
 *   unplannedDays — dates with clock records but no schedule at all
 */
export function findAnomalies({ records, scheduled, scheduledDays, now = new Date(), tolerance = ANOMALY_TOLERANCE_MIN }) {
  const entries = [];
  const byRecord = new Map();
  const addToRecord = (id, iss) => { if (!byRecord.has(id)) byRecord.set(id, []); byRecord.get(id).push(iss); };
  const planDays = scheduledDays || new Set(scheduled.map(p => p.date));
  const unplannedDays = new Set();

  // Normalise
  const recs = records.map(r => {
    const start = toDate(r.clockIn);
    const end = toDate(r.clockOut) || (isOpen(r) ? now : start);
    return start ? { r, start, end, day: isoDay(start) } : null;
  }).filter(Boolean);
  // The same shift assigned twice (an old planner double-booking) is one shift, not double the hours
  const seenSched = new Set();
  const scheds = scheduled.filter(p => p.status !== 'cancelled' && p.startTime && p.endTime)
    .filter(p => { const k = `${p.userId}|${p.date}|${p.startTime}|${p.endTime}`; if (seenSched.has(k)) return false; seenSched.add(k); return true; })
    .map(p => ({ p, ...schedWindow(p), day: p.date }));

  // ── Record-only checks (no schedule needed) ──
  const mistaps = new Set();
  recs.forEach(({ r, start, end }) => {
    if (!isOpen(r) && minutes(end - start) < MISTAP_MIN) {
      mistaps.add(r.id);
      const iss = issue('mis-tap', '0 min', `Clocked in and out at ${fmtClock(start)} — usually a mis-tap`);
      addToRecord(r.id, iss);
      entries.push(entry(r.userId, r.userName, isoDay(start), start, [iss], [r], [], null, fmtRange(start, end)));
      return;
    }
    const recIssues = [];
    if (isOpen(r) && minutes(now - start) > OPEN_TOO_LONG_MIN) {
      recIssues.push(issue('open-long', `Open ${fmtDur(minutes(now - start))}`, `Still clocked in after ${fmtDur(minutes(now - start))} — forgot to clock out?`));
    }
    const worked = recordWorked(r, start, end);
    if (!isOpen(r) && worked > LONG_SHIFT_MIN) {
      recIssues.push(issue('long-shift', `${fmtDur(worked)} shift`, `${fmtDur(worked)} worked in one clock-in — check the clock-out`));
    }
    const brk = breakMinutesWithin(r.breaks || [], start, end, now);
    if (brk > LONG_BREAK_MIN) {
      recIssues.push(issue('long-break', `Break ${fmtDur(brk)}`, `Took ${fmtDur(brk)} of breaks`));
    }
    if (recIssues.length) {
      recIssues.forEach(i => addToRecord(r.id, i));
      entries.push(entry(r.userId, r.userName, isoDay(start), start, recIssues, [r], [], null, fmtRange(start, toDate(r.clockOut))));
    }
  });

  // Overlapping records of the same person (duplicates, or an edit that now clashes)
  const live = recs.filter(x => !mistaps.has(x.r.id));
  const byUserAll = groupBy(live, x => x.r.userId);
  Object.values(byUserAll).forEach(list => {
    list.sort((a, b) => a.start - b.start);
    for (let i = 0; i < list.length; i++) {
      for (let j = i + 1; j < list.length && list[j].start < list[i].end; j++) {
        const a = list[i], b = list[j];
        const ov = minutes(Math.min(a.end, b.end) - Math.max(a.start, b.start));
        if (ov > 1) {
          const iss = issue('overlap', 'Overlaps', `${fmtRange(a.start, toDate(a.r.clockOut))} and ${fmtRange(b.start, toDate(b.r.clockOut))} overlap by ${fmtDur(ov)} — hours would count twice`);
          addToRecord(a.r.id, iss); addToRecord(b.r.id, iss);
          entries.push(entry(a.r.userId, a.r.userName, isoDay(a.start), a.start, [iss], [a.r, b.r], [], null, null));
        }
      }
    }
  });

  // ── Schedule comparison, per person per day ──
  const recsByKey = groupBy(live, x => `${x.r.userId}|${x.day}`);
  const schedByKey = groupBy(scheds, x => `${x.p.userId}|${x.day}`);
  const keys = new Set([...Object.keys(recsByKey), ...Object.keys(schedByKey)]);

  keys.forEach(key => {
    const [userId, day] = key.split('|');
    const dayRecs = recsByKey[key] || [];
    const daySched = schedByKey[key] || [];
    if (!planDays.has(day)) { if (dayRecs.length) unplannedDays.add(day); return; }
    const userName = dayRecs[0]?.r.userName || daySched[0]?.p.userName || 'Staff';

    // Group items that overlap in time
    const items = [...daySched.map(s => ({ t: 's', ...s })), ...dayRecs.map(r => ({ t: 'r', ...r }))];
    const parent = items.map((_, i) => i);
    const find = i => (parent[i] === i ? i : (parent[i] = find(parent[i])));
    for (let i = 0; i < items.length; i++) {
      for (let j = i + 1; j < items.length; j++) {
        if (items[i].start < items[j].end && items[j].start < items[i].end) parent[find(i)] = find(j);
      }
    }
    const groups = groupBy(items.map((it, i) => ({ it, g: find(i) })), x => x.g);

    Object.values(groups).forEach(members => {
      const S = members.map(m => m.it).filter(x => x.t === 's').sort((a, b) => a.start - b.start);
      const R = members.map(m => m.it).filter(x => x.t === 'r').sort((a, b) => a.start - b.start);

      if (!R.length) {
        // Assigned but nobody came
        S.forEach(s => {
          if (now >= s.end) {
            entries.push(entry(userId, userName, day, s.start, [issue('no-show', 'No-show', `Assigned ${fmtRange(s.start, s.end)} but never clocked in`)], [], [s.p], fmtRange(s.start, s.end), null));
          } else if (minutes(now - s.start) > tolerance) {
            entries.push(entry(userId, userName, day, s.start, [issue('missing', 'Not in yet', `Shift started ${fmtDur(minutes(now - s.start))} ago — not clocked in`)], [], [s.p], fmtRange(s.start, s.end), null));
          }
        });
        return;
      }

      if (!S.length) {
        // Came without an assignment (at least not at this time)
        R.forEach(x => {
          const other = daySched.length
            ? `Assigned ${daySched.map(s => fmtRange(s.start, s.end)).join(' & ')} that day — worked ${fmtRange(x.start, toDate(x.r.clockOut))} instead`
            : `Not on the schedule — worked ${fmtRange(x.start, toDate(x.r.clockOut))}`;
          const iss = issue('unscheduled', daySched.length ? 'Off schedule' : 'Not scheduled', other);
          addToRecord(x.r.id, iss);
          entries.push(entry(userId, userName, day, x.start, [iss], [x.r], [], null, fmtRange(x.start, toDate(x.r.clockOut))));
        });
        return;
      }

      // Both: compare the group's actual window with its assigned window
      const sStart = S[0].start, sEnd = S.reduce((m, s) => (s.end > m ? s.end : m), S[0].end);
      const aStart = R[0].start, anyOpen = R.some(x => isOpen(x.r));
      const aEnd = R.reduce((m, x) => (x.end > m ? x.end : m), R[0].end);
      const iss = [];
      const inDiff = minutes(aStart - sStart);
      if (inDiff < -tolerance) iss.push(issue('early-in', `In ${fmtDur(inDiff)} early`, `Clocked in ${fmtDur(inDiff)} before the ${fmtClock(sStart)} start`));
      if (inDiff > tolerance) iss.push(issue('late-in', `In ${fmtDur(inDiff)} late`, `Clocked in ${fmtDur(inDiff)} after the ${fmtClock(sStart)} start`));
      const outDiff = minutes(aEnd - sEnd);
      if (anyOpen) {
        if (outDiff > tolerance) iss.push(issue('late-out', `Still in +${fmtDur(outDiff)}`, `Still clocked in ${fmtDur(outDiff)} after the ${fmtClock(sEnd)} end`));
      } else {
        if (outDiff < -tolerance) iss.push(issue('early-out', `Left ${fmtDur(outDiff)} early`, `Clocked out ${fmtDur(outDiff)} before the ${fmtClock(sEnd)} end`));
        if (outDiff > tolerance) iss.push(issue('late-out', `Stayed ${fmtDur(outDiff)} late`, `Clocked out ${fmtDur(outDiff)} after the ${fmtClock(sEnd)} end`));
      }
      // One clock-in running through the break between two assigned shifts
      for (let i = 0; i + 1 < S.length; i++) {
        const gapStart = S[i].end, gapEnd = S[i + 1].start;
        if (gapEnd > gapStart && R.some(x => x.start < gapEnd && x.end > gapStart) && minutes(gapEnd - gapStart) > 15) {
          iss.push(issue('covers-break', 'No break punch', `One clock-in runs through the ${fmtRange(gapStart, gapEnd)} break between shifts — didn't clock out for the ${fmtDur(minutes(gapEnd - gapStart))} gap`));
          break;
        }
      }
      // Total time, when each end alone was within tolerance
      if (!anyOpen && !iss.some(i => ['early-in', 'late-in', 'early-out', 'late-out'].includes(i.kind))) {
        const worked = R.reduce((sum, x) => sum + recordWorked(x.r, x.start, x.end), 0);
        const planned = S.reduce((sum, s) => sum + minutes(s.end - s.start), 0);
        const diff = worked - planned;
        if (diff > tolerance) iss.push(issue('over-hours', `+${fmtDur(diff)} over`, `Worked ${fmtDur(worked)} against ${fmtDur(planned)} assigned`));
        if (diff < -tolerance) iss.push(issue('under-hours', `${fmtDur(diff)} short`, `Worked ${fmtDur(worked)} against ${fmtDur(planned)} assigned`));
      }
      if (iss.length) {
        R.forEach(x => iss.forEach(i => addToRecord(x.r.id, i)));
        entries.push(entry(userId, userName, day, aStart, iss, R.map(x => x.r), S.map(s => s.p),
          S.map(s => fmtRange(s.start, s.end)).join(' & '), R.map(x => fmtRange(x.start, toDate(x.r.clockOut))).join(' & ')));
      }
    });
  });

  // ── Weekly overtime on actual hours ──
  const byWeek = groupBy(live, x => `${x.r.userId}|${mondayISO(x.start)}`);
  Object.entries(byWeek).forEach(([key, list]) => {
    const worked = list.reduce((sum, x) => sum + recordWorked(x.r, x.start, x.end), 0);
    if (worked > WEEKLY_OVERTIME_MIN) {
      const [userId, monday] = key.split('|');
      entries.push({
        ...entry(userId, list[0].r.userName, monday, new Date(`${monday}T00:00:00`),
          [issue('overtime', `${fmtDur(worked)} week`, `${fmtDur(worked)} worked in the week of ${fmtDayLabel(monday)} — over 40h`)], [], [], null, null),
        key: `ot|${key}`, weekly: true,
      });
    }
  });

  entries.forEach(e => { e.reviewed = isReviewed(e); });
  const rank = { high: 0, medium: 1 };
  entries.sort((a, b) => (b.start - a.start) || (rank[topSeverity(a)] - rank[topSeverity(b)]));
  return { entries, byRecord, unplannedDays: [...unplannedDays].sort() };
}

function entry(userId, userName, day, start, issues, recordDocs, schedDocs, schedText, actualText) {
  const ids = [...recordDocs.map(r => r.id), ...schedDocs.map(p => `s:${p.id}`)];
  return {
    key: `${issues.map(i => i.kind).join('+')}|${userId}|${day}|${ids.join(',')}`,
    userId, userName, day, dayLabel: fmtDayLabel(day), start, issues,
    recordIds: recordDocs.map(r => r.id), scheduledIds: schedDocs.map(p => p.id),
    records: recordDocs, scheduledDocs: schedDocs, schedText, actualText,
  };
}

/** Marked OK: every issue kind is in a review on its records (or, for no-shows, on the assigned shift). */
function isReviewed(e) {
  if (e.weekly) return false;
  const kinds = e.issues.map(i => i.kind);
  if (!e.records.length) return e.scheduledDocs.some(p => p.anomalyReview);
  const reviewed = new Set(e.records.flatMap(r => r.anomalyReview?.kinds || []));
  return kinds.every(k => reviewed.has(k));
}

export function topSeverity(e) {
  return e.issues.some(i => i.severity === 'high') ? 'high' : 'medium';
}

function groupBy(list, keyFn) {
  const out = {};
  list.forEach(x => { const k = keyFn(x); (out[k] = out[k] || []).push(x); });
  return out;
}

/** Which filter chip an issue belongs to. */
export const ANOMALY_GROUPS = [
  { id: 'unscheduled', label: 'Not scheduled', kinds: ['unscheduled'] },
  { id: 'timing', label: 'Late / early', kinds: ['early-in', 'late-in', 'early-out', 'late-out', 'missing'] },
  { id: 'hours', label: 'Hours', kinds: ['over-hours', 'under-hours', 'long-shift', 'overtime'] },
  { id: 'no-show', label: 'No-show', kinds: ['no-show'] },
  { id: 'records', label: 'Overlaps & mis-taps', kinds: ['overlap', 'mis-tap', 'covers-break', 'open-long'] },
  { id: 'breaks', label: 'Breaks', kinds: ['long-break'] },
];
